import React, { useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { getRoomCategoryOptionsFromSchema, useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';

import { BED_TYPE_OPTIONS, formatBedType } from './utils';

interface AddRoomFormValues {
  bedType: string;
  category: string;
  description: string;
  name: string;
}

interface Props {
  onAdd: (values: AddRoomFormValues) => void;
  onClose: () => void;
  open: boolean;
  propertyId?: string;
}

const DEFAULT_FORM_VALUES: AddRoomFormValues = {
  bedType: 'KingBed',
  category: '',
  description: '',
  name: '',
};

function getDefaultCategory(categoryOptions: Array<string>) {
  return categoryOptions[0] ?? '';
}

export default function AddRoomDialog({ onAdd, onClose, open, propertyId }: Props) {
  const { data: roomTypesSchema } = useRoomTypesSchema({ propertyId, enabled: open });
  const roomCategoryOptions = useMemo(() => getRoomCategoryOptionsFromSchema(roomTypesSchema), [roomTypesSchema]);
  const hasResetFormForOpenRef = useRef(false);
  const [formValues, setFormValues] = useState<AddRoomFormValues>({
    ...DEFAULT_FORM_VALUES,
    category: getDefaultCategory(roomCategoryOptions),
  });

  useEffect(() => {
    if (!open) {
      hasResetFormForOpenRef.current = false;
      return;
    }

    if (hasResetFormForOpenRef.current) {
      return;
    }

    hasResetFormForOpenRef.current = true;
    setFormValues({
      ...DEFAULT_FORM_VALUES,
      category: getDefaultCategory(roomCategoryOptions),
    });
  }, [open, roomCategoryOptions]);

  useEffect(() => {
    if (!open || formValues.category || roomCategoryOptions.length === 0) {
      return;
    }

    setFormValues((currentValues) => ({ ...currentValues, category: getDefaultCategory(roomCategoryOptions) }));
  }, [formValues.category, open, roomCategoryOptions]);

  const canAddRoom =
    !!formValues.name.trim() && !!formValues.category && !!formValues.bedType && !!formValues.description.trim();

  const handleAdd = () => {
    const name = formValues.name.trim();
    const description = formValues.description.trim();

    if (!canAddRoom) {
      return;
    }

    onAdd({
      ...formValues,
      description,
      name,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add room</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <FormLabel>Name</FormLabel>
            <TextField
              required
              fullWidth
              autoFocus
              placeholder="e.g. Ocean Front Villa"
              value={formValues.name}
              onChange={(event) => setFormValues((currentValues) => ({ ...currentValues, name: event.target.value }))}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <FormLabel>Category</FormLabel>
              <TextField
                select
                required
                fullWidth
                value={formValues.category}
                onChange={(event) =>
                  setFormValues((currentValues) => ({ ...currentValues, category: event.target.value }))
                }
              >
                {roomCategoryOptions.map((categoryOption) => (
                  <MenuItem key={categoryOption} value={categoryOption}>
                    {categoryOption}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack spacing={1} sx={{ flex: 1 }}>
              <FormLabel>Bed type</FormLabel>
              <TextField
                select
                required
                fullWidth
                value={formValues.bedType}
                onChange={(event) =>
                  setFormValues((currentValues) => ({ ...currentValues, bedType: event.target.value }))
                }
              >
                {BED_TYPE_OPTIONS.map((bedType) => (
                  <MenuItem key={bedType} value={bedType}>
                    {formatBedType(bedType)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <FormLabel>Description</FormLabel>
            <TextField
              required
              fullWidth
              multiline
              minRows={3}
              placeholder="Describe the room experience and key features"
              value={formValues.description}
              onChange={(event) =>
                setFormValues((currentValues) => ({
                  ...currentValues,
                  description: event.target.value,
                }))
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canAddRoom} onClick={handleAdd}>
          Add room
        </Button>
      </DialogActions>
    </Dialog>
  );
}
